import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { Document, Page, pdfjs } from 'react-pdf';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const OFFICE_PREVIEW_EXTENSIONS = ['xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx'];
const IMAGE_PREVIEW_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
const DOCX_PAGE_WIDTH = 816;

/** Read-only Office embed (no ribbon). view.aspx shows the full editor chrome. */
export const getOfficePreviewEmbedUrl = (fileUrl) => {
  const params = new URLSearchParams({
    src: fileUrl,
    wdPrint: '0',
    wdEmbedCode: '0',
  });
  return `https://view.officeapps.live.com/op/embed.aspx?${params}`;
};

const safeDecodeURIComponent = (value = '') => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const getFileExtension = (filename = '', fileUrl = '') => {
  const cleanName = filename.split('?')[0].split('#')[0];
  if (cleanName.includes('.')) {
    return cleanName.split('.').pop().toLowerCase();
  }

  const decodedUrl = safeDecodeURIComponent(fileUrl).split('?')[0].split('#')[0];
  if (decodedUrl.includes('.')) {
    return decodedUrl.split('.').pop().toLowerCase();
  }

  return '';
};

/** Symbol/Wingdings bullet glyphs from docx-preview do not render reliably on macOS. */
const fixDocxBulletStyles = (container) => {
  container.querySelectorAll('style').forEach((styleEl) => {
    styleEl.textContent = styleEl.textContent.replace(
      /(p\.attachment-docx-content-num-[\w-]+:before\s*\{)([^}]*)\}/gi,
      (match, opener, body) => {
        if (/content:\s*"[^"]*counter\(/i.test(body) || !/counter-increment/i.test(body)) {
          return match;
        }

        const fixedBody = body
          .replace(/font-family:\s*[^;]+;/gi, '')
          .replace(
            /content:\s*"[^"]*";/i,
            'content: "\\2022\\00a0"; font-family: Arial, Helvetica, sans-serif; font-weight: 800;'
          );

        return `${opener}${fixedBody}}`;
      }
    );
  });
};

const AttachmentPreviewContent = ({
  url,
  filename,
  pageNumber,
  onPageNumberChange,
  onPdfLoadSuccess,
  showControls = true,
  fullPdfPreview = false,
  containedPreview = false,
  setMaxHeight = true,
  shareDocumentPage = false,
}) => {
  const extension = useMemo(() => getFileExtension(filename, url), [filename, url]);
  const [internalPdfPage, setInternalPdfPage] = useState(1);
  const [pdfPages, setPdfPages] = useState(0);
  const [pdfZoom, setPdfZoom] = useState(1);
  const [docxLoading, setDocxLoading] = useState(false);
  const [docxError, setDocxError] = useState('');
  const docxContainerRef = useRef(null);
  const docxPreviewRef = useRef(null);
  const pdfFullPreviewRef = useRef(null);
  const [pdfPageWidth, setPdfPageWidth] = useState(null);
  const [docxContainerWidth, setDocxContainerWidth] = useState(null);
  const docxScale = useMemo(() => {
    if (!docxContainerWidth || docxContainerWidth >= DOCX_PAGE_WIDTH) {
      return 1;
    }

    return docxContainerWidth / DOCX_PAGE_WIDTH;
  }, [docxContainerWidth]);
  const isControlledPdfPage = typeof pageNumber === 'number';
  const pdfPage = isControlledPdfPage ? pageNumber : internalPdfPage;
  const setPdfPage = (value) => {
    const nextValue = typeof value === 'function' ? value(pdfPage) : value;
    if (isControlledPdfPage) {
      onPageNumberChange?.(nextValue);
    } else {
      setInternalPdfPage(nextValue);
    }
  };

  useEffect(() => {
    if (!isControlledPdfPage) {
      setInternalPdfPage(1);
    }
    setPdfPages(0);
    setPdfZoom(1);
    setPdfPageWidth(null);
    setDocxContainerWidth(null);
  }, [url, isControlledPdfPage]);

  useEffect(() => {
    if (!fullPdfPreview || extension !== 'pdf' || !pdfFullPreviewRef.current) return;

    const updateWidth = () => {
      setPdfPageWidth(pdfFullPreviewRef.current?.clientWidth ?? null);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(pdfFullPreviewRef.current);

    return () => resizeObserver.disconnect();
  }, [extension, fullPdfPreview, url]);

  useLayoutEffect(() => {
    if (extension !== 'docx' || !containedPreview) {
      return;
    }

    const updateWidth = () => {
      const width = docxPreviewRef.current?.clientWidth;

      if (width > 0) {
        setDocxContainerWidth(width);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const element = docxPreviewRef.current;

    if (!element) {
      return;
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [containedPreview, docxLoading, extension, url]);

  useLayoutEffect(() => {
    const container = docxContainerRef.current;

    if (extension !== 'docx' || !containedPreview || docxLoading || !container) {
      return;
    }

    const resetWrapperStyles = (wrapper) => {
      wrapper.style.transform = '';
      wrapper.style.transformOrigin = '';
      wrapper.style.width = '';
    };

    const unwrapScaleFrame = () => {
      const scaleFrame = container.querySelector(':scope > .attachment-docx-scale-frame');

      if (!scaleFrame) {
        return;
      }

      const contentWrapper = scaleFrame.querySelector('.attachment-docx-content-wrapper');

      if (contentWrapper) {
        resetWrapperStyles(contentWrapper);
        container.insertBefore(contentWrapper, scaleFrame);
      }

      scaleFrame.remove();
    };

    if (docxScale >= 1) {
      unwrapScaleFrame();
      return;
    }

    let contentWrapper = container.querySelector(':scope > .attachment-docx-content-wrapper');

    if (!contentWrapper) {
      contentWrapper = container.querySelector('.attachment-docx-scale-frame > .attachment-docx-content-wrapper');
    }

    if (!contentWrapper) {
      return;
    }

    let scaleFrame = container.querySelector(':scope > .attachment-docx-scale-frame');

    if (!scaleFrame) {
      scaleFrame = document.createElement('div');
      scaleFrame.className = 'attachment-docx-scale-frame';
      container.appendChild(scaleFrame);
      scaleFrame.appendChild(contentWrapper);
    } else if (contentWrapper.parentElement !== scaleFrame) {
      scaleFrame.appendChild(contentWrapper);
    }

    const naturalHeight = contentWrapper.scrollHeight;
    scaleFrame.style.width = `${DOCX_PAGE_WIDTH * docxScale}px`;
    scaleFrame.style.height = `${naturalHeight * docxScale}px`;
  }, [containedPreview, docxContainerWidth, docxLoading, docxScale, extension, url]);

  useEffect(() => {
    if (extension !== 'docx' || !docxContainerRef.current) return;

    let cancelled = false;
    const currentContainer = docxContainerRef.current;
    currentContainer.innerHTML = '';
    setDocxError('');
    setDocxLoading(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load DOCX file');
        }
        return response.blob();
      })
      .then((blob) =>
        renderAsync(blob, currentContainer, null, {
          className: 'attachment-docx-content',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: true,
          renderHeaders: true,
          renderFooters: true,
        })
      )
      .then(() => {
        if (!cancelled) {
          fixDocxBulletStyles(currentContainer);
          setDocxLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDocxError('Unable to preview this DOCX file.');
          setDocxLoading(false);
        }
      });

    return () => {
      cancelled = true;
      currentContainer.innerHTML = '';
    };
  }, [extension, url]);

  if (extension === 'pdf' && fullPdfPreview) {
    return (
      <div ref={pdfFullPreviewRef} className='attachment-pdf-full-preview'>
        <Document
          file={url}
          onLoadSuccess={({ numPages }) => {
            setPdfPages(numPages);
            onPdfLoadSuccess?.({ numPages });
          }}
          loading={<p className='paragraph-general mb-0 h-100'>Loading document...</p>}
          error={<p className='paragraph-general mb-0'>Unable to preview this PDF file.</p>}
        >
          {pdfPageWidth &&
            Array.from({ length: pdfPages }, (_, index) => (
              <Page
                key={`page-${index + 1}`}
                pageNumber={index + 1}
                width={pdfPageWidth}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                className='attachment-pdf-page'
              />
            ))}
        </Document>
      </div>
    );
  }

  if (extension === 'pdf') {
    return (
      <div className={`p-3 border rounded`}>
        {showControls && (
          <div className='d-flex align-items-center justify-content-between mb-3'>
            <div className='d-flex align-items-center'>
              <button
                type='button'
                className='general-button px-3 py-2 mr-2'
                disabled={pdfPage <= 1}
                onClick={() => setPdfPage((prev) => Math.max(1, prev - 1))}
              >
                Prev page
              </button>
              <span className='paragraph-p3 main-subtle-text'>
                {pdfPages > 0 ? `${pdfPage}/${pdfPages}` : 'Loading...'}
              </span>
              <button
                type='button'
                className='general-button px-3 py-2 ml-2'
                disabled={pdfPages === 0 || pdfPage >= pdfPages}
                onClick={() => setPdfPage((prev) => Math.min(pdfPages, prev + 1))}
              >
                Next page
              </button>
            </div>
            <div className='d-flex align-items-center'>
              <button
                type='button'
                className='general-button px-3 py-2 mr-2'
                disabled={pdfZoom <= 0.7}
                onClick={() => setPdfZoom((prev) => Math.max(0.7, +(prev - 0.1).toFixed(2)))}
              >
                -
              </button>
              <span className='paragraph-p3 main-subtle-text'>{Math.round(pdfZoom * 100)}%</span>
              <button
                type='button'
                className='general-button px-3 py-2 ml-2'
                disabled={pdfZoom >= 2}
                onClick={() => setPdfZoom((prev) => Math.min(2, +(prev + 0.1).toFixed(2)))}
              >
                +
              </button>
            </div>
          </div>
        )}
        <div
          className={`${setMaxHeight ? 'attachment-pdf-canvas-wrapper' : `overflow-auto  ${shareDocumentPage ? 'attachment-pdf-fill-preview' : ''}`}`}
        >
          <Document
            file={url}
            onLoadSuccess={({ numPages }) => {
              setPdfPages(numPages);
              onPdfLoadSuccess?.({ numPages });
              setPdfPage((prev) => Math.min(prev, numPages));
            }}
            loading={<p className='paragraph-general mb-0 p-3'>Loading PDF...</p>}
            error={<p className='paragraph-general mb-0 p-3'>Unable to preview this PDF file.</p>}
          >
            <Page
              pageNumber={pdfPage}
              scale={pdfZoom}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              className='attachment-pdf-page'
            />
          </Document>
        </div>
      </div>
    );
  }

  if (extension === 'docx') {
    const shouldScaleDocx = containedPreview && docxScale < 1;
    const docxRenderContainer = (
      <div
        ref={docxContainerRef}
        className={`attachment-docx-wrapper${containedPreview ? ' attachment-docx-wrapper-contained' : ''}${
          shouldScaleDocx ? ' attachment-docx-wrapper-scaled' : ''
        }`}
        style={{
          ...(shouldScaleDocx ? { '--docx-scale': docxScale } : {}),
        }}
      />
    );

    if (containedPreview) {
      return (
        <div ref={docxPreviewRef} className='attachment-preview-height-container'>
          {docxLoading && <p className='paragraph-general mb-3'>Loading DOCX...</p>}
          {docxError && <p className='paragraph-general mb-3'>{docxError}</p>}
          {docxRenderContainer}
        </div>
      );
    }

    return (
      <div className='p-3 border rounded'>
        {docxLoading && <p className='paragraph-general mb-3'>Loading DOCX...</p>}
        {docxError && <p className='paragraph-general mb-3'>{docxError}</p>}
        {docxRenderContainer}
      </div>
    );
  }

  if (IMAGE_PREVIEW_EXTENSIONS.includes(extension)) {
    const image = <img src={url} alt={filename} className='attachment-preview-image' />;

    if (containedPreview) {
      return <div className='attachment-preview-height-container'>{image}</div>;
    }

    return <div className='p-3 border rounded'>{image}</div>;
  }

  if (OFFICE_PREVIEW_EXTENSIONS.includes(extension)) {
    const officePreview = (
      <iframe
        src={getOfficePreviewEmbedUrl(url)}
        title={`preview-${filename}`}
        className={`attachment-preview-iframe${containedPreview ? ' attachment-preview-iframe-contained' : ''}`}
        loading='lazy'
        referrerPolicy='no-referrer'
      />
    );

    if (containedPreview) {
      return <div className='attachment-preview-height-container'>{officePreview}</div>;
    }

    return officePreview;
  }

  return <p className='paragraph-general mb-0'>Preview is not available for this file type.</p>;
};

export default AttachmentPreviewContent;
